// ABOUTME: React Query hook for fetching delivery window configuration from the backend API.
// ABOUTME: Calls GET /tuc-configs/delivery-windows, finds config for selected scope, and transforms to form-ready shape.

import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'

import {api} from '../../../../App.store'
import {
  DAY_CODE_TO_ID,
  DayCode,
  DayIdentifier,
  DeliveryWindowApiResponse,
  DeliveryWindowConfig,
  DeliveryWindowFormValues,
} from '../types/deliveryWindow'
import {asNotInherited, emptyApiRow} from '../utils/emptyConfig'

/** Strips seconds from API time string: "06:00:00" → "06:00" */
const toDisplayTime = (apiTime: string): string => apiTime.slice(0, 5)

const transformResponse = (response: DeliveryWindowApiResponse): DeliveryWindowConfig => {
  const timeZone = response.timeZone?.value ?? ''
  const businessDays = (response.businessDays?.value?.activeDays ?? [])
    .map((code) => DAY_CODE_TO_ID[code as DayCode])
    .filter((d): d is DayIdentifier => Boolean(d))
  const slotManagementDays = (response.operatingSchedule?.value?.standard?.standardDays ?? [])
    .map((code) => DAY_CODE_TO_ID[code as DayCode])
    .filter((d): d is DayIdentifier => Boolean(d))
  const standardOperatingDays = slotManagementDays
  const standardHours = {
    onlineDelivery: {
      start: toDisplayTime(response.operatingSchedule?.value?.standard?.onlineDeliveryHours?.start ?? ''),
      end: toDisplayTime(response.operatingSchedule?.value?.standard?.onlineDeliveryHours?.end ?? ''),
    },
    defaultDelivery: {
      start: toDisplayTime(response.operatingSchedule?.value?.standard?.defaultDeliveryHours?.start ?? ''),
      end: toDisplayTime(response.operatingSchedule?.value?.standard?.defaultDeliveryHours?.end ?? ''),
    },
    slotManagement: {start: '', end: ''},
  }
  const nonStandardHours = (response.operatingSchedule?.value?.nonStandard ?? []).reduce(
    (acc, ns) => {
      const dayId = DAY_CODE_TO_ID[ns.day as DayCode]
      if (dayId) {
        acc[dayId] = {
          onlineDelivery: {
            start: toDisplayTime(ns.onlineDeliveryHours?.start ?? ''),
            end: toDisplayTime(ns.onlineDeliveryHours?.end ?? ''),
          },
          defaultDelivery: {
            start: toDisplayTime(ns.defaultDeliveryHours?.start ?? ''),
            end: toDisplayTime(ns.defaultDeliveryHours?.end ?? ''),
          },
          slotManagement: {start: '', end: ''},
        }
      }
      return acc
    },
    {} as Record<string, Record<string, {start: string; end: string}>>
  )
  const deliveryConstraints = {
    minDeliveryWindow: response.minDeliveryWindow?.value ?? 0,
    minFixDeliveryWindow: response.minFixDeliveryWindow?.value ?? 0,
    stepInterval: response.stepInterval?.value ?? 0,
  }
  const formValues: DeliveryWindowFormValues = {
    timeZone, businessDays, slotManagementDays, standardOperatingDays, standardHours, nonStandardHours, deliveryConstraints,
  }
  return {
    id: response.id,
    ...formValues,
    overrides: {
      timeZone: response.timeZone?.isOverridden ?? false,
      businessDays: response.businessDays?.isOverridden ?? false,
      operatingSchedule: response.operatingSchedule?.isOverridden ?? false,
      minDeliveryWindow: response.minDeliveryWindow?.isOverridden ?? false,
      minFixDeliveryWindow: response.minFixDeliveryWindow?.isOverridden ?? false,
      stepInterval: response.stepInterval?.isOverridden ?? false,
    },
    parentValues: formValues,
    _meta: {
      scopeId: response.scopeId,
      isInherited: !(response.businessDays?.isOverridden ?? false),
      resolvedFrom: null,
      createdAt: response._meta.createdAt,
      updatedAt: response._meta.updatedAt,
      createdBy: response._meta.createdBy,
      updatedBy: response._meta.updatedBy,
    },
  }
}

export const useDeliveryWindowConfig = (
  countryId: string | undefined,
  businessLine: string | undefined,
  scopeId: string | undefined,
  rootScopeId?: string | undefined
): {data: DeliveryWindowConfig | undefined; isLoading: boolean; error: Error | null} => {
  const {data: rawData, isLoading, error} = useQuery<DeliveryWindowApiResponse[], Error>({
    queryKey: ['deliveryWindowConfig', countryId, businessLine],
    queryFn: async () => {
      const res = await api.get<DeliveryWindowApiResponse[]>('/tuc-configs/delivery-windows', {
        params: {countryId, businessLine: businessLine!.toUpperCase()},
      })
      return res.data ?? []
    },
    enabled: Boolean(countryId && businessLine && scopeId),
    staleTime: 30_000,
  })

  const data = useMemo(() => {
    if (!rawData || !scopeId) return undefined

    const rootRawConfig = rootScopeId
      ? rawData.find((r) => r.scopeId === rootScopeId)
      : undefined
    const rootConfig = rootRawConfig ? transformResponse(rootRawConfig) : undefined
    const parentValues = rootConfig
      ? {
          timeZone: rootConfig.timeZone,
          businessDays: rootConfig.businessDays,
          slotManagementDays: rootConfig.slotManagementDays,
          standardOperatingDays: rootConfig.standardOperatingDays,
          standardHours: rootConfig.standardHours,
          nonStandardHours: rootConfig.nonStandardHours,
          deliveryConstraints: rootConfig.deliveryConstraints,
        }
      : undefined

    // Direct match — this scope has its own config
    const match = rawData.find((r) => r.scopeId === scopeId)
    if (match) {
      const config = transformResponse(match)
      return parentValues ? {...config, parentValues} : config
    }

    // No direct config — show inherited values from root/parent
    if (!rootRawConfig || !rootConfig) {
      return asNotInherited(transformResponse(emptyApiRow<DeliveryWindowApiResponse>(scopeId)))
    }

    return {
      ...rootConfig,
      id: undefined,
      parentValues: parentValues ?? {
        timeZone: rootConfig.timeZone,
        businessDays: rootConfig.businessDays,
        slotManagementDays: rootConfig.slotManagementDays,
        standardOperatingDays: rootConfig.standardOperatingDays,
        standardHours: rootConfig.standardHours,
        nonStandardHours: rootConfig.nonStandardHours,
        deliveryConstraints: rootConfig.deliveryConstraints,
      },
      _meta: {...rootConfig._meta, isInherited: true},
      overrides: {
        timeZone: false,
        businessDays: false,
        operatingSchedule: false,
        minDeliveryWindow: false,
        minFixDeliveryWindow: false,
        stepInterval: false,
      },
    }
  }, [rawData, scopeId, rootScopeId])

  return {data, isLoading, error}
}
